import { FC, useEffect, useRef, useState } from 'react'
import { IBanner } from '../../utils/types'
import { Link } from 'react-router-dom'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import classNames from 'classnames'
import cn from './index.module.css'

interface IBannerCard {
  banner: IBanner
  className?: string
  onButtonClick?: () => void
}

const defaultColors = [
  { name: 'accent', code: '#6e368c' },
  { name: 'accentFirst', code: '#ed6b69' },
  { name: 'blueGray', code: '#e6ecf2' },
  { name: 'gray', code: '#a3a3a5' },
  { name: 'white', code: '#ffffff' },
  { name: 'violet', code: '#ae9be4' },
  { name: 'violetDark', code: '#9a82dd' },
  { name: 'blueDeep', code: '#0cc1f3' },
  { name: 'yellowDeep', code: '#feca06' },
  { name: 'pinkDeep', code: '#ee347d' },
  { name: 'greenDeep', code: '#4bb042' },
]

function mapDefaultColor(value: string): string {
  const color = defaultColors.find((c) => c.name === value)
  return color ? color.code : value
}

const BannerCard: FC<IBannerCard> = ({ banner, className, onButtonClick }) => {
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    if (!cardRef?.current) return

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setIsMobile((cardRef?.current?.clientWidth ?? 0) < 767)
      }
    })

    observer.observe(cardRef?.current)

    return () => observer.disconnect()
  }, [])

  const hasButton = banner?.textButton

  const clampedTitleStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    overflow: 'hidden',
  }

  const clampedDescriptionStyle: React.CSSProperties = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: hasButton ? 3 : 5,
    overflow: 'hidden',
  }

  return (
    <div
      className={classNames(cn.cardWrapper, className)}
      style={{ backgroundColor: mapDefaultColor(banner.background) }}
      ref={cardRef}
      onClick={() => setIsExpanded((prev) => !prev)}
    >
      <div className={cn.contentWrapper}>
        {/* {banner?.subtitle && (
          <div className={cn.subTitle}>{banner?.subtitle}</div>
        )} */}
        {
          !!banner?.title && (
            <div
              className={cn.title}
              style={{
                color: mapDefaultColor(banner.colorText),
                ...(isExpanded ? {} : clampedTitleStyle),
              }}
            >
              {banner?.title}
            </div>
          )
        }
        {banner?.description && (
          <div
            className={cn.description}
            style={{
              color: mapDefaultColor(banner.colorText),
              ...(isExpanded ? {} : clampedDescriptionStyle),
            }}
          >
            {banner?.description}
          </div>
        )}
        {hasButton && (
          <a
            href={banner?.linkButton ?? ''}
            className={cn.link}
            onClick={(e) => {
              e?.stopPropagation()
              if (!!onButtonClick) {
                e?.preventDefault()
              }
            }}
          >
            <SecondaryButton
              className={cn.button}
              onClick={() => {
                onButtonClick?.()
              }}
              styles={{
                backgroundColor: mapDefaultColor(banner?.colorButton ?? ''),
                color: mapDefaultColor(banner?.colorTextButton ?? ''),
              }}
            >
              {banner?.textButton}
            </SecondaryButton>
          </a>
        )}
      </div>
      {banner.image && !isMobile && (
        <div className={cn.imageWrapper}>
          <img className={cn.image} src={banner.image} alt={banner.title} />
        </div>
      )}
    </div>
  )
}

export default BannerCard
